import { useIntl } from '@capsicum/common';
import { ActivityIndicator, Goat } from '@capsicum/common-web';
import { useSurveyList } from '@capsicum/features/survey/shared';
import styled from 'styled-components';

import { SurveyListCard } from '../survey-list-card/SurveyListCard';

type SurveyCardListProps = {
	filterBy: 'closed' | 'opened';
	partnerId: string;
	testID: string;
};

export const SurveyCardList = ({ filterBy, partnerId, testID }: SurveyCardListProps) => {
	const surveyList = useSurveyList(partnerId);
	const { $t } = useIntl();

	const { error, isLoading, listSurveys, partnerImages, partnerName, partnerShortName } = surveyList;

	if (isLoading) {
		return (
			<S.LoadingContainer>
				<ActivityIndicator />
			</S.LoadingContainer>
		);
	}

	if (error || !listSurveys) {
		return <Goat image="ankward" testID="surveyList-errorGoat-image" title={$t('survey.list.error')} />;
	}

	if (!listSurveys[filterBy].length) {
		return (
			<Goat image="ankward" testID="surveyList-errorGoat-image" title={$t('survey.noCurrentOrUpcomingPolls')} />
		);
	}

	return (
		<S.ListWrapper data-testid={testID}>
			{listSurveys[filterBy].map(
				({
					isBound,
					isSurveyClosed,
					isSurveyScheduled,
					labelProps,
					question,
					surveyId,
					tokenLimit,
					userVote
				}) => {
					return (
						<S.CardContainer key={surveyId}>
							<SurveyListCard
								hasVoted={userVote}
								isBound={isBound}
								isSurveyClosed={isSurveyClosed}
								isSurveyScheduled={isSurveyScheduled}
								labelProps={labelProps}
								mainText={question}
								partnerId={partnerId}
								surveyId={surveyId}
								teamLogoUri={partnerImages?.partnerLogo?.url ?? ''}
								teamName={partnerName ?? ''}
								teamNameShort={partnerShortName ?? ''}
								testID={`${testID}Card-${surveyId}`}
								tokenLimit={tokenLimit}
							/>
						</S.CardContainer>
					);
				}
			)}
		</S.ListWrapper>
	);
};

namespace S {
	export const CardContainer = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};

		&:first-child {
			margin-top: ${({ theme }) => theme.spacings.xlarge};
		}
	`;

	export const LoadingContainer = styled.div`
		display: flex;
		width: 100%;
		justify-content: center;
		padding-top: ${({ theme }) => theme.spacings.xxxlarge};
	`;

	export const ListWrapper = styled.div`
		padding: 0 ${({ theme }) => theme.spacings.small};
	`;
}
